import { useMemo, useState } from "react";
import { Search, Plus, Minus } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import PageHero from "../components/PageHero";
import Eyebrow from "../components/shared/Eyebrow";
import FinalCTA from "../components/sections/FinalCTA";
import { faqService } from "../services/faqService";
import { useRemoteData } from "../hooks/useRemoteData";
import AsyncSection, { SkeletonRows } from "../components/shared/AsyncSection";
import { cn } from "../lib/utils";

export default function Faqs() {
  const [q, setQ] = useState("");
  const [openKey, setOpenKey] = useState("g0-i0");

  const { data: groups, loading } = useRemoteData(() => faqService.list(), [], {
    initialValue: [],
  });

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    const source = groups ?? [];
    if (!query) return source;

    return source
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (it) =>
            it.q.toLowerCase().includes(query) || it.a.toLowerCase().includes(query)
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [q, groups]);

  return (
    <>
      <PageHero
        eyebrow="FAQs"
        headline={"Everything you might\nwant to ask."}
        intro="Common questions from customers, grouped by topic."
        image="/generated/service_disinfection.png"
        imageAlt="Emilio's Solutions cleaner disinfecting an office desk area"
        compact
      />

      <section
        className="bg-ink py-16 md:py-20 border-t border-charcoal/50"
        data-testid="faq-page"
      >
        <div className="container-emilio">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <Eyebrow>Search</Eyebrow>
              <div className="mt-5 flex items-center gap-2 border border-charcoal focus-within:border-emerald px-3 py-2">
                <Search className="w-4 h-4 text-mutedtext" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search FAQs"
                  aria-label="Search FAQs"
                  data-testid="faq-search"
                  className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-mutedtext"
                />
              </div>
              <div className="mt-8 text-mutedtext text-sm leading-relaxed">
                Can't find your answer? Send us a message on WhatsApp — we usually reply within an hour.
              </div>
            </div>
            <div className="lg:col-span-8">
              <AsyncSection
                loading={loading}
                isEmpty={filtered.length === 0}
                skeleton={<SkeletonRows count={5} />}
                emptyTitle={q ? "No FAQs match your search" : "No questions published yet"}
                emptyMessage={
                  q
                    ? "Try a different word, or message us on WhatsApp."
                    : "Message us on WhatsApp and we will answer directly."
                }
              >
                {filtered.map((g, gi) => (
                  <div key={g.group} className="mb-10">
                    <div className="text-emerald text-xs font-display font-bold uppercase tracking-[0.22em] mb-4">
                      {g.group}
                    </div>
                    <div className="divide-y divide-charcoal border-y border-charcoal">
                      {g.items.map((it, ii) => {
                        const key = `g${gi}-i${ii}`;
                        const open = openKey === key;
                        return (
                          <div key={ii} data-testid={`faq-page-item-${gi}-${ii}`}>
                            <button
                              type="button"
                              onClick={() => setOpenKey(open ? "" : key)}
                              className="w-full flex items-center justify-between text-left py-5 md:py-6 gap-6"
                            >
                              <span
                                className={cn(
                                  "font-display font-semibold text-lg md:text-xl transition-colors",
                                  open ? "text-emerald" : "text-white"
                                )}
                              >
                                {it.q}
                              </span>
                              <span className="w-8 h-8 grid place-items-center border border-charcoal shrink-0">
                                {open ? (
                                  <Minus className="w-4 h-4 text-emerald" />
                                ) : (
                                  <Plus className="w-4 h-4 text-white" />
                                )}
                              </span>
                            </button>
                            <AnimatePresence initial={false}>
                              {open && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: "auto", opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.4 }}
                                  className="overflow-hidden"
                                >
                                  <p className="pb-6 text-mutedtext text-base leading-relaxed max-w-2xl">
                                    {it.a}
                                  </p>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </AsyncSection>
            </div>
          </div>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
